import { useMemo } from 'react';
import { resourceUrlMapping } from '@/constant/resourceUrlMapping';
import { config } from '@/config';
import { explorerAnalytics } from '@/lib/analytics';
import { useExplorer } from '@/hooks/useExplorer';
import { snakeCase } from 'change-case-all';
import { ExternalLink } from 'lucide-react';

type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };
type JsonObject = { [key: string]: JsonValue };

const isJsonObject = (value: unknown): value is JsonObject => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const ChargebeeSiteLink = () => {
  const { responsePayload, endpoint, siteName } = useExplorer();

  const chargebeeAppUrl = useMemo(() => {
    if (!responsePayload || !endpoint || !siteName) {
      return null;
    }

    if (!isJsonObject(responsePayload)) {
      return null;
    }

    // Extract resource name from endpoint URL (e.g., /customers/{id} -> customers)
    const urlParts = endpoint.url.split('/').filter(Boolean);
    const resourceNameFromUrl = urlParts[0]?.toLowerCase();

    if (!resourceNameFromUrl) {
      return null;
    }

    const parentMapping = resourceUrlMapping[resourceNameFromUrl];

    if (!parentMapping) {
      return null;
    }

    //Followed the same conversion as in EndpointSelector.tsx
    const operationId = snakeCase(endpoint.name);

    if (!parentMapping.allowedOperations.includes(operationId)) {
      return null;
    }

    let matchedMapping = parentMapping;

    if (parentMapping.subResources) {
      const dataValue = responsePayload[parentMapping.responseObjectKey];

      if (!dataValue || !isJsonObject(dataValue)) {
        return null;
      }

      let subResourceMatch = false;
      for (const [, subMapping] of Object.entries(parentMapping.subResources)) {
        const allRequirementsMatch = subMapping.require_fields.every(
          (requirement) => {
            const fieldValue = dataValue[requirement.field];
            if (fieldValue === undefined || fieldValue === null) return false;
            if (
              requirement.value !== undefined &&
              fieldValue !== requirement.value
            ) {
              return false;
            }
            return true;
          }
        );

        if (allRequirementsMatch) {
          matchedMapping = subMapping;
          subResourceMatch = true;
          break;
        }
      }

      if (!subResourceMatch) {
        return null;
      }
    }

    // Get the data object for validation and URL building
    const dataValue = responsePayload[matchedMapping.responseObjectKey];

    if (!dataValue || !isJsonObject(dataValue)) {
      return null;
    }

    // Validate all requirements (for non-subResource cases)
    if (!parentMapping.subResources) {
      const allRequirementsMatch = matchedMapping.require_fields.every(
        (requirement) => {
          const fieldValue = dataValue[requirement.field];

          if (fieldValue === undefined || fieldValue === null) {
            return false;
          }

          if (
            requirement.value !== undefined &&
            fieldValue !== requirement.value
          ) {
            return false;
          }

          return true;
        }
      );

      if (!allRequirementsMatch) {
        return null;
      }
    }

    // Build URL by replacing placeholders
    let url = matchedMapping.chargebeeAppUrl;
    url = url.replace('{protocol}', config.cbProtocol);
    url = url.replace('{site}', siteName);
    url = url.replace('{domain}', config.cbDomain);

    // Replace field placeholders in URL (only for fields without value requirements)
    matchedMapping.require_fields.forEach((requirement) => {
      if (requirement.value === undefined) {
        const value = dataValue[requirement.field];
        url = url.replace(`{${requirement.field}}`, String(value));
      }
    });

    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('utm_source', 'api-explorer');
      return urlObj.toString();
    } catch {
      return url;
    }
  }, [responsePayload, endpoint, siteName]);

  if (!chargebeeAppUrl) return null;

  return (
    <>
      <span className="text-gray-400">|</span>
      <div className="flex w-full items-center gap-2">
        <a
          href={chargebeeAppUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline text-[12px] font-medium flex items-center gap-1 transition-colors duration-200"
          onClick={() => {
            explorerAnalytics.chargebeeSiteLinkClicked({
              siteName: siteName || '',
              resource: endpoint?.url?.split('/').filter(Boolean)[0] || '',
              endpoint: endpoint?.name || '',
            });
          }}
        >
          <span>View in Chargebee Site</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </>
  );
};

export default ChargebeeSiteLink;
